import { useState } from 'react';
import useSWR, { mutate as refresh } from 'swr';
import { useCommissionAccess } from '@/hooks/useCommissionAccess';
import { useAppStore } from '@/stores';
import { get, TableType } from '@/lib/supabase';
import { supabaseClient } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button';
import { getFullName } from '@revelate/utils';
import type { SignedInCompany } from '@revelate/types';

export function TeamResponsibility({ teamId }: { teamId: number }) {
  const { data: access, mutate } = useCommissionAccess();
  const user = useAppStore(state => state.currentUser);
  const { data: company } = useSWR(['companies' as TableType, String(user?.company_id)], ([table, id]): Promise<SignedInCompany> => get(table, id));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const team = access?.teams.find(t => t.id === teamId);
  if (!team || !company) return null;
  const descendants = new Set([teamId]);
  let previous = 0;
  while (previous !== descendants.size) {
    previous = descendants.size;
    access?.teams.forEach(t => { if (t.parent_team_id && descendants.has(t.parent_team_id)) descendants.add(t.id); });
  }
  const direct = company.teams.find(t => t.id === teamId)?.users.map(u => u.id) ?? [];
  const inherited = company.users.filter(u => team.user_ids.includes(u.id) && !direct.includes(u.id) && u.id !== team.manager_id);
  return <form key={`${teamId}-${team.parent_team_id}-${team.manager_id}`} className="space-y-4" onSubmit={async event => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSaving(true); setError('');
    try {
      const { error } = await supabaseClient.rpc('set_commission_team_settings', {
        target_team_id: teamId, parent_id: form.get('parent') ? Number(form.get('parent')) : null,
        manager_user_id: form.get('manager') || null,
      });
      if (error) throw error;
      await mutate();
      await refresh(key => Array.isArray(key) && ['companies', 'teams', 'commission-access'].includes(String(key[0])));
    } catch (error) { setError(error && typeof error === 'object' && 'message' in error ? String(error.message) : 'Could not save team responsibility.'); }
    finally { setSaving(false); }
  }}>
    <h3 className="text-lg font-medium">Responsibility</h3>
    <label className="block text-sm">Parent team<select name="parent" defaultValue={team.parent_team_id ?? ''} className="block mt-2 border rounded-md p-2 w-full">
      <option value="">No parent team</option>{access?.teams.filter(t => !descendants.has(t.id)).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
    </select></label>
    <label className="block text-sm">Manager<select name="manager" defaultValue={team.manager_id ?? ''} className="block mt-2 border rounded-md p-2 w-full">
      <option value="">No manager assigned</option>{company.users.filter(u => ['manager', 'admin', 'super_admin'].includes(u.role)).map(u => <option key={u.id} value={u.id}>{getFullName(u)}</option>)}
    </select></label>
    <p className="text-sm text-muted-foreground">This manager and managers above this team can view and approve its members. Only admins give final approval.</p>
    {inherited.length > 0 && <div className="text-sm"><p className="font-medium">Included through subteams ({inherited.length})</p><ul>{inherited.map(u => <li key={u.id}>{getFullName(u)}</li>)}</ul></div>}
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    <Button disabled={saving}>{saving ? 'Saving…' : 'Save responsibility'}</Button>
  </form>;
}
